import { useEffect, useState } from 'react';
import { Activity, Users, Building2, MessageSquare, Download, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';
import { API_BASE } from '../../lib/api';

const RANGES = [
  { v: 7, label: '7 días' }, { v: 30, label: '30 días' },
  { v: 180, label: '6 meses' }, { v: 365, label: '1 año' },
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics/summary?days=${days}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [days]);

  const downloadPdf = () => {
    const token = localStorage.getItem('cnib_token');
    const url = `${API_BASE}/admin/analytics/export.pdf?days=${days}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const u = URL.createObjectURL(b);
        const a = document.createElement('a'); a.href = u; a.download = `cnib-analytics-${days}d.pdf`; a.click();
        URL.revokeObjectURL(u);
      });
  };

  const stats = [
    { icon: Activity, label: 'En vivo (5 min)', value: data?.live_users ?? '…', color: 'bg-emerald-500' },
    { icon: Users, label: `Visitas (${days}d)`, value: data?.visits_window ?? '…', color: 'bg-cnib-blue' },
    { icon: Users, label: 'Socios activos', value: data?.total_members ?? '…', color: 'bg-orange-500' },
    { icon: Building2, label: 'Propiedades', value: data?.total_properties ?? '…', color: 'bg-cyan-500' },
    { icon: MessageSquare, label: 'Posts feed', value: data?.total_posts ?? '…', color: 'bg-violet-500' },
    { icon: CheckCircle2, label: 'Usuarios totales', value: data?.total_users ?? '…', color: 'bg-slate-700' },
  ];

  const max = Math.max(1, ...(data?.daily_series || []).map((d) => d.visits));

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Analítica</p>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">Panel de estadísticas</h1>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" data-testid="analytics-range-tabs">
          {RANGES.map((r) => (
            <button
              key={r.v}
              onClick={() => setDays(r.v)}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${days === r.v ? 'bg-cnib-navy text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
              data-testid={`analytics-range-${r.v}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <button
          onClick={downloadPdf}
          data-testid="analytics-export-pdf"
          className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2 text-sm font-bold text-white hover:bg-orange-600"
        >
          <Download className="h-4 w-4" /> Exportar PDF
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="analytics-stats-grid">
        {stats.map((s) => (
          <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-3">
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${s.color} text-white`}>
                <s.icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{s.label}</p>
                <p className="font-heading text-2xl font-extrabold text-slate-900">{s.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="font-heading text-base font-extrabold text-slate-900">Visitas por día</h2>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-cnib-blue" /></div>
        ) : (data?.daily_series || []).length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">Sin visitas registradas aún.</p>
        ) : (
          <div className="mt-4 space-y-1.5">
            {(data?.daily_series || []).map((d) => (
              <div key={d.day} className="flex items-center gap-3 text-xs">
                <span className="w-20 shrink-0 text-slate-500">{d.day}</span>
                <div className="flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-3 rounded-full bg-gradient-to-r from-cnib-blue to-orange-500" style={{ width: `${(d.visits / max) * 100}%` }} />
                </div>
                <span className="w-10 shrink-0 text-right font-bold text-slate-700">{d.visits}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
